import { Routes } from "@angular/router";
import { authGuard } from "./guards/auth.guard";
import { publicGuard } from "./guards/public.guard";
import { AppShellComponent } from "./layout/app-shell.component";
import { devOnlyGuard } from "./guards/dev-only.guard";
import { landingGuard } from "./guards/landing.guard";
import { rolesGuard } from "./guards/roles.guard";
import { UserRole } from "./models/permissions.model";

export const routes: Routes = [
	{
		path: "login",
		canActivate: [publicGuard],
		loadComponent: () => import("./pages/login/login.component").then((m) => m.LoginComponent),
	},
	{
		path: "auth/email-link-signin",
		canActivate: [publicGuard],
		loadComponent: () =>
			import("./pages/auth/email-link-signin/email-link-signin.component").then(
				(m) => m.EmailLinkSigninComponent,
			),
	},
	{
		path: "auth/wrong-device",
		canActivate: [publicGuard],
		loadComponent: () =>
			import("./pages/auth/wrong-device/wrong-device.component").then(
				(m) => m.WrongDeviceComponent,
			),
	},
	{
		path: "",
		component: AppShellComponent,
		canActivate: [authGuard],
		children: [
			{
				path: "",
				redirectTo: "overview",
				pathMatch: "full",
			},
			{
				path: "profile",
				// A handful of short fields; full width leaves it adrift.
				data: { breadcrumb: "Profile", surface: "narrow" },
				loadComponent: () =>
					import("./pages/profile/profile.component").then((m) => m.ProfileComponent),
			},
			{
				path: "overview",
				loadComponent: () =>
					import("./pages/overview/overview.component").then((m) => m.OverviewComponent),
				// landingGuard runs first: an admin who belongs to exactly one
				// organization is sent into it rather than shown an overview
				// built for people who manage several.
				canActivate: [landingGuard, rolesGuard],
				data: {
					breadcrumb: "Overview",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-admins",
				loadComponent: () =>
					import("./pages/global-management/admins/global-admins-list/global-admins-list.component").then(
						(m) => m.GlobalAdminsListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "Global Admins",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-admins/new",
				loadComponent: () =>
					import("./pages/global-management/admins/global-admin-form/global-admin-form.component").then(
						(m) => m.GlobalAdminFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					surface: "form",
					breadcrumb: "New",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-admins/:id/edit",
				loadComponent: () =>
					import("./pages/global-management/users/user-form/user-form.component").then(
						(m) => m.UserFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "Edit",
					surface: "form",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "users",
				loadComponent: () =>
					import("./pages/global-management/users/users-list/users-list.component").then(
						(m) => m.UsersListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "All Users",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "users/new",
				loadComponent: () =>
					import("./pages/global-management/users/user-form/user-form.component").then(
						(m) => m.UserFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "New",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "users/:id/edit",
				loadComponent: () =>
					import("./pages/global-management/users/user-form/user-form.component").then(
						(m) => m.UserFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "Edit",
					surface: "form",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "mail-delivery-stats",
				loadComponent: () =>
					import("./pages/mail-delivery/mail-delivery.component").then(
						(m) => m.MailDeliveryComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "Mail Delivery Stats",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "organizations",
				loadComponent: () =>
					import("./pages/organizations/list/organizations-list.component").then(
						(m) => m.OrganizationsListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: "Organizations",
					roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
					permissionContext: "organizations",
				},
			},
			{
				path: "organizations/new",
				loadComponent: () =>
					import("./pages/organizations/form/organization-form.component").then(
						(m) => m.OrganizationFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					surface: "form",
					breadcrumb: "New",
					roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
					permissionContext: "organizations",
				},
			},
			{
				// A sibling of organizations/:id rather than a child, so its path
				// carries all three segments and the breadcrumb has to name each
				// one. As a single string it produced one crumb for the whole
				// path, and a lone crumb is hidden — so the page had none at all.
				path: "organizations/:id/user-imports",
				loadComponent: () =>
					import("./pages/imports/import.component").then((m) => m.ImportComponent),
				canActivate: [rolesGuard],
				data: {
					breadcrumb: ["Organizations", { fallback: "Organization" }, "User Imports"],
					roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
					permissionContext: "organizations",
				},
			},
			{
				path: "organizations/:id",
				canActivate: [rolesGuard],
				data: {
					breadcrumb: ["Organizations", { fallback: "Organization" }],
					roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
					permissionContext: "organizations",
				},
				children: [
					{
						path: "",
						redirectTo: "dashboard",
						pathMatch: "full",
					},
					{
						path: "dashboard",
						data: { breadcrumb: "Dashboard" },
						loadComponent: () =>
							import("./pages/organizations/organization-management/dashboard/dashboard.component").then(
								(m) => m.DashboardComponent,
							),
					},
					{
						// Details was folded into the dashboard: both pages loaded
						// the same organization and one of them only described it.
						path: "details",
						redirectTo: "dashboard",
						pathMatch: "full",
					},
					{
						// The organization is edited in place on the dashboard now,
						// so this page is gone. Both old paths still resolve.
						path: "edit",
						redirectTo: "dashboard",
						pathMatch: "full",
					},
					{ path: "details/edit", redirectTo: "dashboard", pathMatch: "full" },
					{
						path: "admins",
						loadComponent: () =>
							import("./pages/organizations/organization-management/admin/admin.component").then(
								(m) => m.AdminComponent,
							),
						canActivate: [rolesGuard],
						data: {
							breadcrumb: "Admins",
							roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
							permissionContext: "organization",
						},
					},
					// Retired org paths. `admin` predates the refresh; `administrators`
					// and `mail-delivery` are from earlier phases of it. All three
					// are in browser history and in links people have sent.
					{ path: "admin", redirectTo: "admins", pathMatch: "full" },
					{ path: "administrators", redirectTo: "admins", pathMatch: "full" },
					{ path: "mail-delivery", redirectTo: "mail-delivery-stats", pathMatch: "full" },
					{
						path: "mail-delivery-stats",
						data: { breadcrumb: "Mail Delivery Stats" },
						loadComponent: () =>
							import("./pages/mail-delivery/mail-delivery.component").then(
								(m) => m.MailDeliveryComponent,
							),
					},
					{
						path: "respondents",
						loadComponent: () =>
							import("./pages/organizations/organization-management/respondents/respondents.component").then(
								(m) => m.RespondentsComponent,
							),
						canActivate: [rolesGuard],
						data: {
							breadcrumb: "Respondents",
							roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
							permissionContext: "organization",
						},
					},
					{
						path: "surveys",
						data: { breadcrumb: "Surveys" },
						loadComponent: () =>
							import("./pages/organizations/organization-management/surveys/surveys.component").then(
								(m) => m.SurveysComponent,
							),
					},
					{
						// "Add survey" attached an existing survey to the
						// organization, which is what creating one already does.
						// Nothing ever linked to it; the redirect is for anyone
						// who kept the URL.
						path: "surveys/add",
						redirectTo: "surveys",
						pathMatch: "full",
					},
					{
						path: "surveys/create",
						loadComponent: () =>
							import("./pages/organizations/organization-management/surveys/create/create-survey.component").then(
								(m) => m.CreateSurveyComponent,
							),
						canActivate: [rolesGuard],
						data: {
							// Two segments, so each needs a label — as one string
							// the whole path became a single "Create" crumb with
							// no Surveys above it.
							breadcrumb: ["Surveys", "Create"],
							surface: "form",
							roles: [UserRole.ADMIN],
							permissionContext: "global",
						},
					},
					{
						path: "surveys/edit/:surveyId",
						data: { breadcrumb: "Edit", surface: "form" },
						loadComponent: () =>
							import("./pages/organizations/organization-management/surveys/edit/edit-survey.component").then(
								(m) => m.EditSurveyComponent,
							),
					},
				],
			},
			// Retired paths. These are deep-linked from emails and browser history,
			// so they redirect rather than 404. Most specific first — a shorter
			// pattern placed above a longer one would shadow it.
			{ path: "home", redirectTo: "overview", pathMatch: "full" },
			{ path: "dashboard", redirectTo: "overview", pathMatch: "full" },
			{
				path: "global-management/admins/new",
				redirectTo: "global-admins/new",
				pathMatch: "full",
			},
			{
				path: "global-management/admins/:id/edit",
				redirectTo: "global-admins/:id/edit",
				pathMatch: "full",
			},
			{ path: "global-management/admins", redirectTo: "global-admins", pathMatch: "full" },
			{ path: "global-management/users/new", redirectTo: "users/new", pathMatch: "full" },
			{
				path: "global-management/users/:id/edit",
				redirectTo: "users/:id/edit",
				pathMatch: "full",
			},
			{ path: "global-management/users", redirectTo: "users", pathMatch: "full" },
			{ path: "global-management", redirectTo: "overview", pathMatch: "full" },
			{ path: "mail-delivery", redirectTo: "mail-delivery-stats", pathMatch: "full" },
			{
				path: "global-management/mail-delivery",
				redirectTo: "mail-delivery-stats",
				pathMatch: "full",
			},
			{
				path: "organizations/:id/imports",
				redirectTo: "organizations/:id/user-imports",
				pathMatch: "full",
			},
			{
				// A Material icon reference for developers. devOnlyGuard closes the
				// URL in production: rolesGuard alone would let any global admin
				// reach it, including a customer's.
				path: "icons",
				loadComponent: () =>
					import("./pages/icons/icons.component").then((m) => m.IconsComponent),
				canActivate: [devOnlyGuard, rolesGuard],
				data: {
					breadcrumb: "Icons",
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
		],
	},
];
